import { useEffect } from "react";

/**
 * Lightweight SEO hook. Sets document.title, meta description, canonical link,
 * robots directive, OG + Twitter tags and optionally injects one or more
 * JSON-LD scripts. Cleans up on unmount so SPA navigation doesn't leak
 * previous page's tags.
 */
export function useSEO({
  title,
  description,
  canonical,
  robots,          // e.g. "noindex, follow"
  ogImage,
  jsonLd = [],
} = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const upsert = (selector, tagName, attrs) => {
      let el = document.head.querySelector(selector);
      const created = !el;
      if (!el) {
        el = document.createElement(tagName);
        Object.entries(attrs.set).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      const prev = {};
      Object.entries(attrs.upd).forEach(([k, v]) => {
        prev[k] = el.getAttribute(k);
        if (v !== undefined && v !== null) el.setAttribute(k, v);
      });
      return { el, created, prev };
    };

    const trackers = [];
    if (description) trackers.push(upsert('meta[name="description"]', "meta",
      { set: { name: "description" }, upd: { content: description } }));
    if (robots) trackers.push(upsert('meta[name="robots"]', "meta",
      { set: { name: "robots" }, upd: { content: robots } }));
    if (title) trackers.push(upsert('meta[property="og:title"]', "meta",
      { set: { property: "og:title" }, upd: { content: title } }));
    if (description) trackers.push(upsert('meta[property="og:description"]', "meta",
      { set: { property: "og:description" }, upd: { content: description } }));
    trackers.push(upsert('meta[property="og:type"]', "meta",
      { set: { property: "og:type" }, upd: { content: "website" } }));
    if (canonical) trackers.push(upsert('meta[property="og:url"]', "meta",
      { set: { property: "og:url" }, upd: { content: canonical } }));
    if (ogImage) trackers.push(upsert('meta[property="og:image"]', "meta",
      { set: { property: "og:image" }, upd: { content: ogImage } }));
    if (title) trackers.push(upsert('meta[name="twitter:title"]', "meta",
      { set: { name: "twitter:title" }, upd: { content: title } }));
    if (description) trackers.push(upsert('meta[name="twitter:description"]', "meta",
      { set: { name: "twitter:description" }, upd: { content: description } }));
    trackers.push(upsert('meta[name="twitter:card"]', "meta",
      { set: { name: "twitter:card" }, upd: { content: "summary_large_image" } }));
    if (canonical) trackers.push(upsert('link[rel="canonical"]', "link",
      { set: { rel: "canonical" }, upd: { href: canonical } }));

    const ldScripts = (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).filter(Boolean).map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      return s;
    });

    return () => {
      document.title = prevTitle;
      trackers.forEach((t) => {
        if (!t) return;
        if (t.created) t.el.remove();
        else {
          Object.entries(t.prev).forEach(([k, v]) => {
            if (v === null || v === undefined) t.el.removeAttribute(k);
            else t.el.setAttribute(k, v);
          });
        }
      });
      ldScripts.forEach((s) => s.remove());
    };
  }, [title, description, canonical, robots, ogImage, JSON.stringify(jsonLd)]);
}
